import { useState, useEffect } from "react";
import { getSalons, createSalon, deleteSalon } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { Plus, Store, Trash2, Calendar, ShieldAlert } from "lucide-react";

const Salons = () => {
  const { isAdmin } = useAuth();
  const [salons, setSalons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newSalonName, setNewSalonName] = useState("");
  const [addLoading, setAddLoading] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchSalons();
  }, []);

  const fetchSalons = async () => {
    setLoading(true);
    try {
      const response = await getSalons();
      setSalons(response.data);
    } catch (error) {
      toast.error("Failed to fetch salons");
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    if (!newSalonName.trim()) {
      toast.error("Please enter a salon name");
      return;
    }
    
    setAddLoading(true);
    try {
      await createSalon({ name: newSalonName.trim() });
      toast.success("Salon added successfully");
      setNewSalonName("");
      setShowAdd(false);
      fetchSalons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add salon");
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteSalon(deleteId);
      toast.success("Salon deleted");
      fetchSalons();
    } catch (error) {
      toast.error("Failed to delete salon");
    } finally {
      setDeleteId(null);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="salons-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Manage Salons</h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin ? "Add and manage your salon locations" : "View salon locations"}
          </p>
        </div>
        {isAdmin && (
          <Button 
            onClick={() => setShowAdd(true)} 
            className="rounded-full bg-orange-500 hover:bg-orange-600"
            data-testid="add-salon-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Salon
          </Button>
        )}
      </div>

      {/* Admin notice for non-admin users */}
      {!isAdmin && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="flex items-center gap-3 py-4">
            <ShieldAlert className="w-5 h-5 text-orange-600" />
            <p className="text-sm text-orange-800">Only admin users can add or delete salons. Contact your admin for changes.</p>
          </CardContent>
        </Card>
      )}

      {/* Salons Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : salons.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {salons.map((salon) => (
            <Card key={salon.id} className="border-border/50 shadow-sm hover:shadow-md transition-all group">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                      <Store className="w-6 h-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-lg text-slate-900">{salon.name}</h3>
                      <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                        <Calendar className="w-3 h-3" />
                        Added {format(new Date(salon.created_at), "MMM d, yyyy")}
                      </p>
                    </div>
                  </div>
                  {isAdmin && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteId(salon.id)}
                      className="h-8 w-8 rounded-full text-muted-foreground hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-salon-${salon.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="border-border/50 shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mb-4">
              <Store className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="font-heading font-semibold text-lg text-slate-900">No salons yet</h3>
            <p className="text-muted-foreground text-sm mt-1">
              {isAdmin ? "Add your first salon to start tracking sales" : "No salons have been added yet"}
            </p>
            {isAdmin && (
              <Button onClick={() => setShowAdd(true)} className="mt-4 rounded-full bg-orange-500 hover:bg-orange-600">
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Salon
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Add Salon Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">Add New Salon</DialogTitle>
            <DialogDescription>
              Enter the name of your salon location
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="salon-name">Salon Name</Label>
              <Input
                id="salon-name"
                placeholder="e.g., Downtown Branch"
                value={newSalonName}
                onChange={(e) => setNewSalonName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                data-testid="salon-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)} className="rounded-full">
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={addLoading} className="rounded-full" data-testid="save-salon-btn">
              {addLoading ? "Adding..." : "Add Salon"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Salon</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this salon? All associated sales records will also be deleted. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-salon">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-salon"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Salons;
